import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import apolloUploadClient from 'apollo-upload-client';
import { WebSocketLink } from 'apollo-link-ws';
import { split, ApolloLink, Observable, FetchResult } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import { onError, ErrorResponse } from 'apollo-link-error';
import axios, { AxiosResponse } from 'axios';

import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { OperationDefinitionNode } from 'graphql';
import bugsnagClient from './bugsnag';
import { actionSetToken } from './store/actions/session.actions';
import { TokenResponse } from './types/TokenResponse';
import store from '@/store/store';
import { namespace as sessionNamespace } from '@/store/stores/session.store';

const getHTTPURI = options => {
  const { operationName } = options;
  if (process.env.VUE_APP_INTERNAL_API_BASE_URL) {
    // Use new Internal API endpoint if it's specified
    return `https://${process.env.VUE_APP_INTERNAL_API_BASE_URL}/graphql?op=${operationName}`;
  }
  // VUE_APP_GRAPHQL_URL is deprecated
  if (process.env.VUE_APP_GRAPHQL_URL) {
    return `https://${process.env.VUE_APP_GRAPHQL_URL}?op=${operationName}`;
  }
  return `http://localhost:3001/graphql?op=${operationName}`;
};

const getWSURI = () => {
  if (process.env.VUE_APP_INTERNAL_API_BASE_URL) {
    // Use new Internal API endpoint if it's specified
    return `wss://${process.env.VUE_APP_INTERNAL_API_BASE_URL}/graphql`;
  }
  // VUE_APP_GRAPHQL_URL is deprecated
  if (process.env.VUE_APP_GRAPHQL_URL) {
    return `wss://${process.env.VUE_APP_GRAPHQL_URL}`;
  }
  return 'ws://localhost:3001/graphql';
};

const uploadClientLink = apolloUploadClient.createUploadLink({
  uri: getHTTPURI,
});

const wsLink = new WebSocketLink({
  uri: getWSURI(),
  options: {
    reconnect: true,
    lazy: true,
    timeout: Number.MAX_SAFE_INTEGER,
    connectionParams: () => ({ Authorization: getAuthToken() }),
  },
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
  if (graphQLErrors) {
    const sessionExpiredErrors: any[] = graphQLErrors.filter(
      (error: any) => error.extensions && error.extensions.code === 'UNAUTHENTICATED'
    );
    const filteredErrors: any[] = graphQLErrors.filter(
      (error: any) => !error.extensions || error.extensions.code !== 'UNAUTHENTICATED'
    );
    if (sessionExpiredErrors.length) {
      return new Observable(
        (
          observer: ZenObservable.Observer<
            FetchResult<{ [key: string]: any }, Record<string, any>, Record<string, any>>
          >
        ) => {
          axios
            .get('/get/settings/token')
            .then((value: AxiosResponse<TokenResponse>) => {
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  authorization: `Bearer ${value.data.access_token}` || null,
                },
              }));
              return value.data.access_token;
            })
            .then((access_token: string) => {
              store.dispatch(`${sessionNamespace}/${actionSetToken}`, access_token);
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };
              forward(operation).subscribe(subscriber);
            })
            .catch(error => {
              // Should logout the user or empty session state
              observer.error(error);
            });
        }
      );
    }
    if (bugsnagClient) {
      filteredErrors.forEach(graphQLError => {
        bugsnagClient.notify(new Error(JSON.stringify(graphQLError)), event => {
          event.groupingHash = networkError;
          event.addMetadata('GraphQL', {
            operationName: operation?.operationName,
            variables: operation?.variables,
            type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
          });
        });
      });
    }
  }
  if (networkError && bugsnagClient) {
    bugsnagClient.notify(new Error(JSON.stringify(networkError)), event => {
      event.groupingHash = JSON.stringify(networkError);
      event.addMetadata('GraphQL', {
        operationName: operation?.operationName,
        variables: operation?.variables,
        type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
      });
    });
  }
});

const getAuthToken = () => {
  const { token = '' } = store.state['session'];
  return `Bearer ${token}`;
};

const breadcrumbLink = new ApolloLink((operation, forward) => {
  if (bugsnagClient) {
    bugsnagClient.leaveBreadcrumb(
      `GraphQL ${(operation.query.definitions[0] as OperationDefinitionNode).operation}: "${
        operation.operationName
      }"`,
      {
        name: operation.operationName,
        type: (operation.query.definitions[0] as OperationDefinitionNode).operation,
        variables: operation.variables,
      }
    );
  }
  return forward(operation);
});

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext({ headers: { authorization: getAuthToken() } });
  return forward(operation);
});

const link = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  uploadClientLink
);

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, breadcrumbLink, errorLink, link]),
  cache: new InMemoryCache(),
});

Vue.use(VueApollo);

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
});
